/* ═══════════════════════════════════════════════════════════════════
   Print A Pal — shared engine. Loaded by index.html and build.html.
   Everything you edit lives in CONFIG at the top.
   ═══════════════════════════════════════════════════════════════════ */
const $ = id => document.getElementById(id);
const esc = s => String(s).replace(/[&<>]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;'}[c]));

/* Two homes, one engine.
     Static (Vercel)  → window.PAP is undefined. Checkout is a cart permalink.
     Shopify theme    → Liquid defines window.PAP with the store's real variant
                        IDs, and checkout uses the store's own AJAX cart, which
                        carries PER-LINE properties. That difference matters:
                        it lifts the one-configured-keychain-per-order ceiling. */
const PAP = window.PAP || null;

/* ══════════════ money ══════════════
   Every price in this file is held in MINOR units (fils), because that is what
   Shopify reports and rounding half a fil is how displayed and charged prices
   drift apart. In the theme the numbers come from Liquid, so the page cannot
   show a price different from the one the customer is charged. */
const CURRENCY = (PAP && PAP.currency) || 'AED';
const money = minor => {
  const v = (minor || 0) / 100;
  return `${CURRENCY} ${Number.isInteger(v) ? v : v.toFixed(2)}`;
};
/** null = we have no Shopify price for it, i.e. it is not purchasable. */
const keychainPrice = caps => PAP ? (PAP.keychain[caps] ? PAP.keychain[caps].p : null)
                                  : (CONFIG.basePrice + CONFIG.perCap * caps) * 100;
const shelfPrice = p => PAP ? (PAP.shelf[p.id] ? PAP.shelf[p.id].p : null)
                            : p.p * 100;
const fromPrice = () => keychainPrice(1);

/* ══════════════ stock ══════════════
   A colour you cannot print is worse than a colour you never offered, so an
   out-of-stock swatch is shown crossed out and refuses to be selected. Matching
   is by name, case-insensitively, because that is what a person types in admin. */
const asSet = v => new Set((Array.isArray(v) ? v : String(v || '').split(','))
  .map(x => String(x).trim().toLowerCase()).filter(Boolean));
let OOS = { filament: new Set() };

/* The cord is NOT a customer choice. Paracord stock varies, so we pick the reel
   that best suits the finished piece when we pack it. This is the colour every
   preview is drawn with — a stand-in, never a promise, and never part of the
   design the customer submits. */
const CORD = '#1A1A1A';

/* ══════════════ the special charm cord ══════════════
   The one cord upgrade we DO sell: a cord strung with little charms — hearts,
   bells, whatever suits the piece. Like the cord colour, WHICH charms is ours
   to choose when we pack it; the customer buys "special charm cord", nothing
   finer. One Shopify product, one variant, a flat price per keychain, sold only
   through the builder. No design carries it by default: `charm` is absent, not
   empty, so every legacy link and cart line reads as the classic cord. */
const CHARM = {id:'special', n:'Special charm'};
/** Price in minor units, or null = not on sale (unpublished, sold out, or the
    theme has no such product) and the card is not offered. */
const charmPrice = () => {
  if (!PAP) return CONFIG.charmPrice * 100;
  const v = PAP.charm;
  return v && v.v && v.av !== false ? v.p : null;
};
const charmOffered = () => charmPrice() != null;
/** A charm from a link or an old basket must be the one we sell today. */
const cleanCharm = id => id === CHARM.id && charmOffered() ? id : undefined;
/** What a design's cord adds to the keychain price. Zero for the classic cord. */
const charmCost = d => d.charm ? (charmPrice() || 0) : 0;
/* Charm cords are a real, finite stock — unlike the classic cord, which is cut
   from a reel. Shopify tracks it; null = untracked, no limit. Everything below
   keeps a basket from asking for more cords than exist, with a word instead of
   a checkout error. Shopify still has the last say at add-to-cart. */
const charmStock = () => PAP && PAP.charm && typeof PAP.charm.q === 'number' ? PAP.charm.q : null;
/** Charm cords already in the basket, optionally ignoring one line (the one being edited). */
const charmInCart = skipUid => cart.reduce((n, i) =>
  n + (i.kind === 'name' && i.design.charm && i.uid !== skipUid ? i.qty : 0), 0);
/** How many more cords the basket can take. Infinity when untracked. */
const charmRoom = (cap, have) => cap == null ? Infinity : Math.max(0, cap - have);

/* The palette itself is editable in the theme editor: one colour per line,
   "Name #HEX". A blank or malformed line is skipped rather than accepted, and
   if nothing at all parses we keep the built-in list — one typo must never
   leave the shop with no colours to choose from. */
function parsePalette(text){
  const out = [], seen = new Set();
  for (const line of String(text || '').split(/\r?\n/)) {
    const m = line.match(/^\s*(.+?)\s+#([0-9a-f]{3}|[0-9a-f]{6})\s*$/i);
    if (!m) continue;
    const n = m[1].trim();
    if (!n || seen.has(n.toLowerCase())) continue;      // duplicate names break stock matching
    seen.add(n.toLowerCase());
    const hx = m[2].length === 3 ? m[2].replace(/./g, c => c + c) : m[2];
    out.push({ n, h: '#' + hx.toUpperCase() });
  }
  return out.length ? out : null;
}

/* Every zone the customer can colour is printed from the same filament, so
   there is one palette and one stock list. */
const inStock  = c => !OOS.filament.has(c.n.toLowerCase());
/** Usable = still in the palette AND in stock. An unrecognised hex means the
    colour has been removed from the list, so it is no longer orderable. */
const hexUsable = hex => {
  const c = CONFIG.filaments.find(x => x.h.toLowerCase() === String(hex).toLowerCase());
  return !!c && inStock(c);
};
const firstInStock = () => CONFIG.filaments.find(inStock);
/* A basket outlives the stock list: a design saved last week can name a colour
   we ran out of yesterday, and Shopify cannot refuse it — colours are line
   properties, not inventory. So every configured line is re-checked against
   TODAY's list wherever the basket is shown or sent, and a line naming a
   retired colour blocks checkout until it is edited (the builder swaps it). */
const staleZones = d => PZONES.filter(z => !hexUsable(d[z]));
const lineStale  = i => i.kind === 'name' && staleZones(i.design).length > 0;
const staleNames = d => [...new Set(staleZones(d).map(z => nameOf(d[z], CONFIG.filaments)))].join(' and ');
const hexOf = name =>
  (CONFIG.filaments.find(c => c.n.toLowerCase() === String(name).toLowerCase()) || {}).h;

/* ══════════════ bundle deals ══════════════ */
/* "2:10" is AED 10 off; "2:10%" is 10 percent off. Both shapes exist because
   Shopify's two discount types behave differently, and the pills have to match
   whichever one is actually configured in admin. */
function parseTiers(text){
  const out = [];
  for (const line of String(text || '').split(/\r?\n/)) {
    const m = line.match(/^\s*(\d+)\s*[:=]\s*(\d+(?:\.\d+)?)\s*(%?)\s*$/);
    if (m && +m[1] > 1)
      out.push(m[3] ? { qty: +m[1], pct: +m[2] } : { qty: +m[1], save: Math.round(+m[2] * 100) });
  }
  return out.sort((a, b) => a.qty - b.qty);
}
let TIERS = [];

/** How many configured keychains are in the basket. Shelf items do not count —
    the deal is on the personalised product. */
const keychainCount = () => cart.reduce((n, i) => n + (i.kind === 'name' ? i.qty : 0), 0);

/** What comes off at this quantity, in minor units. A percentage tier needs the
    value of the keychains it applies to. */
function bundleSaving(n, base){
  const on = base != null ? base : keychainSubtotal();
  let s = 0;
  for (const t of TIERS) if (n >= t.qty) s = t.pct ? Math.round(on * t.pct / 100) : t.save;
  return s;
}
/** Value of the configured keychains in the basket — what a % tier applies to. */
const keychainSubtotal = () =>
  cart.reduce((v, i) => v + (i.kind === 'name' ? i.price * i.qty : 0), 0);
const tierSaving = (t, unit) => t.pct ? Math.round(unit * t.qty * t.pct / 100) : t.save;
/** The next tier up, for "add one more and save" nudges. */
const nextTier = n => TIERS.find(t => t.qty > n) || null;

/* The ladder is priced per keychain, and a keychain's price depends on how many
   keycaps it has. So in the builder it must describe THE design on screen — a
   "1 for AED 45" pill under a AED 53 design contradicts itself. Elsewhere there
   is no design yet, so it falls back to the cheapest and says "from". */
let dealUnit = null;
const setDealUnit = p => { dealUnit = (p == null ? null : p); };

function bundleHeadline(){
  const unit = dealUnit != null ? dealUnit : keychainPrice(1);
  if (unit == null || !TIERS.length) return [];
  return [{ qty: 1, price: unit, save: 0 },
          ...TIERS.map(t => {
            const save = tierSaving(t, unit);
            return { qty: t.qty, price: unit * t.qty - save, save };
          })];
}
/** "for" when we are pricing a real design, "from" when it is a starting price. */
const dealWord = () => dealUnit != null ? 'for' : 'from';

/** Presets the shop can actually print today. If stock has knocked out too many,
    top up with generated combos so the row is never empty — using the same
    contrast maths as the print guard, at a bar set by your own Zayed piece. */
const PZONES = ['letter','cap','base'];
/** Name → hex, dropped entirely if any colour it names is gone or out of stock. */
function resolvePreset(p){
  const out = { n: p.n, sold: p.sold };
  for (const k of PZONES) {
    const h = hexOf(p[k]);
    if (!h || !hexUsable(h)) return null;
    out[k] = h;
  }
  return out;
}
function availablePresets(){
  const out = CONFIG.presets.map(resolvePreset).filter(Boolean);
  if (out.length >= 2) return out;

  const fil = CONFIG.filaments.filter(inStock);
  const seen = new Set(out.map(p => p.letter + p.cap));
  for (const cap of fil) {
    for (const letter of fil) {
      if (out.length >= 4) return out;
      if (letter.h === cap.h || seen.has(letter.h + cap.h)) continue;
      if (contrast(letter.h, cap.h) < 1.8) continue;   // Zayed scores 1.88
      seen.add(letter.h + cap.h);
      out.push({ n: `${letter.n} on ${cap.n}`, letter: letter.h, cap: cap.h, base: cap.h });
    }
  }
  return out;
}

/** A random combo the print guard would pass, never the one on screen. Letters
    clear the same contrast bar the presets do; the base matches the keycaps
    some of the time (one-piece) and frames them the rest. Null only when the
    stock list has too few colours to make anything. */
function surpriseCombo(cur){
  const fil = CONFIG.filaments.filter(inStock);
  const pick = () => fil[Math.floor(Math.random() * fil.length)].h;
  for (let tries = 0; tries < 60; tries++) {
    const letter = pick(), cap = pick();
    if (letter === cap || contrast(letter, cap) < 1.8) continue;
    const base = Math.random() < .4 ? cap : pick();
    if (cur && letter === cur.letter && cap === cur.cap && base === cur.base) continue;
    return { letter, cap, base };
  }
  return null;
}

/** Finishes on offer. One finish means the chooser is pointless — hide it. */
const finishes = () => (PAP && PAP.finishes && PAP.finishes.length)
  ? PAP.finishes : CONFIG.finishes;

/* ══════════════════════════════════════════════════════════════
   CONFIG — everything you'll ever need to edit lives here.
   In the Shopify port this whole object becomes section settings.
   ══════════════════════════════════════════════════════════════ */
const CONFIG = {
  // ⬇ PASTE YOUR REAL VALUES HERE ⬇
  shop: 'your-store.myshopify.com',   // your permanent .myshopify.com domain
  variants: {                          // keycap count → Shopify variant ID
    1:null, 2:null, 3:null, 4:null, 5:null, 6:null, 7:null, 8:null, 9:null, 10:null
  },

  basePrice: 15, perCap: 5, maxCaps: 10,
  // The charm cord add-on, per keychain. In the theme the price comes from the
  // Charm Cord product's variants; this is the static build's fallback, and
  // charmVariantId is its variant ID for the cart permalink.
  charmPrice: 12, charmVariantId: null,
  // The ready-made Flag Day keychain, static build only — the theme reads Shopify.
  flagPrice: 35, flagLive: false, flagOpen: false,   // the static demo's copy of the theme's "Show the Flag Day edition" switch

  // Letter-vs-keycap contrast floor. Calibrate against a real print: your own
  // Zayed piece (magenta on teal) scores 1.88, so anything above ~1.85 would
  // flag a combo you already sell happily. Raise it only after test prints.
  contrastMin: 1.45,

  // Filament stock — used by letter / keycap / base. Add a colour = add a line.
  filaments: [
    {n:'White',     h:'#FFFFFF'}, {n:'Black',     h:'#2E2E33'},
    {n:'Coral',     h:'#F1645A'}, {n:'Magenta',   h:'#DA2080'},
    {n:'Blue',      h:'#4A9BE8'}, {n:'Mint',      h:'#B3DCD6'},
    {n:'Lavender',  h:'#967BB6'}, {n:'Green',     h:'#4CA64C'},
    {n:'Soft Pink', h:'#F4A6C0'}, {n:'Red',       h:'#FF0000'}
  ],
  // Presets name their colours rather than hard-coding hex, so re-mixing a
  // colour in the theme editor updates every preset that uses it instead of
  // silently dropping them. A preset naming a colour you have retired simply
  // stops being offered.
  //
  // These are the shop's BEST SELLERS, not a designer's picks: the nine
  // letter/keycap/base combinations customers ordered most, ranked from the
  // print queue (calc, /api/queue) — 689 orders, 1,956 keychains printed,
  // 22 Aug – 18 Sep 2026. `sold` is orders, not pieces, so one school order of
  // 48 identical keychains counts once. Refresh the list and the numbers with
  //   node scripts/best-sellers.mjs ~/Downloads/queue.json
  // (the JSON is what /api/queue answers), and keep the order descending.
  //
  // Every pair clears the contrast guard by a wide margin — the weakest,
  // White on Soft Pink, scores 1.90 against a 1.45 floor.
  presets: [
    {n:'Lilac',       letter:'White',     cap:'Lavender',  base:'Soft Pink', sold:141},  // 3.62
    {n:'Lagoon',      letter:'White',     cap:'Blue',      base:'Mint',      sold:106},  // 2.94
    {n:'Bubblegum',   letter:'Magenta',   cap:'White',     base:'Soft Pink', sold:96},   // 4.67
    {n:'Blush',       letter:'White',     cap:'Soft Pink', base:'Soft Pink', sold:44},   // 1.90
    {n:'Meadow',      letter:'White',     cap:'Green',     base:'Mint',      sold:42},   // 3.06
    {n:'Petal',       letter:'White',     cap:'Soft Pink', base:'Lavender',  sold:32},   // 1.90
    {n:'Marshmallow', letter:'Soft Pink', cap:'White',     base:'Soft Pink', sold:27},   // 1.90
    {n:'Ocean',       letter:'Blue',      cap:'White',     base:'Blue',      sold:24},   // 2.94
    {n:'Candy',       letter:'Magenta',   cap:'Soft Pink', base:'Mint',      sold:20}    // 2.46
  ],
  // Your catalogue's own three shelves.
  categories: [
    {n:'Fidget Pals', sub:'Little pals for busy hands.'},
    {n:'Play Pals',   sub:'Little pals made for adventures.'},
    {n:'New Pals',    sub:'Fresh off the printer.'}
  ],
  // Shown in the cart just before Checkout, to set expectations while the queue
  // is long. Blank hides it entirely — a banner that is always up says nothing.
  // In the theme this comes from the theme editor.
  busyNote: '',

  // Where "Ask us" items send people. Put your WhatsApp or mailto here.
  contact: 'https://wa.me/9715XXXXXXXX',

  // Colours you have run out of, by name. Listed colours are struck through and
  // cannot be picked. In the theme this comes from the theme editor, not here.
  outOfStockFilament: [],

  // Bundle deals, advertised here and APPLIED BY SHOPIFY. One tier per line,
  // "quantity:saving" — 2:10 means two keychains take AED 10 off. These must
  // match an automatic discount in Shopify admin; this file only does the
  // arithmetic for the pills, it never changes what anyone is charged.
  bundleTiers: '2:10\n3:20',

  // Letter finishes you actually offer. Drop to one and the chooser disappears.
  finishes: ['Raised', 'Engraved'],

  // Your product list, straight off the catalogue. Art is drawn in DOODLE below.
  // variantId null → demo mode. `href` = configurable, send them to the builder.
  // `ask` = quote-based, no cart.
  shelf: [
    {id:'heart',   n:'Heart Pixel Fidget',  sub:'Pixel heart screen, clicky buttons',     p:25, tag:'Fidget Pals', variantId:null},
    {id:'dumpling',n:'Dumpling Clicker',    sub:'One satisfying thunk, pocket sized',     p:30, tag:'Fidget Pals', variantId:null},
    {id:'spiral',  n:'Spiral Twist',        sub:'Twists down, springs back, endlessly',   p:30, tag:'Fidget Pals', variantId:null},
    {id:'keyboard',n:'Keyboard Key Fidget', sub:'Your name in clicky keycaps',            href:'build.html', tag:'Fidget Pals'},
    {id:'custom',  n:'Custom Fidgets',      sub:'Your shape, your colours — just ask',    ask:true, tag:'Fidget Pals'},

    {id:'minicar', n:'Mini Cars',           sub:'Free-rolling wheels, print-in-place',    p:25, tag:'Play Pals',   variantId:null},
    {id:'balloon', n:'Balloon Cars',        sub:'Blow it up, let it go, watch it fly',    p:30, tag:'Play Pals',   variantId:null},
    {id:'flexi',   n:'Flexi Pals',          sub:'Dragons, dinosaurs, animals & more',     p:45, tag:'Play Pals',   variantId:null},

    {id:'names',   n:'Personalised Names',  sub:'Any name, in keys that click',          href:'build.html', tag:'New Pals'},
    {id:'colours', n:'Custom Colours',      sub:'"Nice Day!" tags in your own palette',   p:20, tag:'New Pals',    variantId:null},
    {id:'party',   n:'Party Favours',       sub:'Bulk pals for the whole guest list',     ask:true, tag:'New Pals'},
    {id:'school',  n:'School Rewards',      sub:'Name, class and age — reusable yearly',  p:55, tag:'New Pals',    variantId:null},
    {id:'business',n:'Business Gifts',      sub:'Branded pals for clients and staff',     ask:true, tag:'New Pals'},
    {id:'oneoff',  n:'One-of-a-Kind Prints',sub:'Got an idea? We will print it',          ask:true, tag:'New Pals'}
  ]
};

/* ══════════════ colour maths — one hex per zone, rest derived ══════════════ */
const hex2rgb = h => [1,3,5].map(i => parseInt(h.slice(i,i+2),16));
const clamp = v => Math.max(0, Math.min(255, Math.round(v)));
/** amt < 0 darkens toward black, amt > 0 lightens toward white. */
function shade(hex, amt){
  // Say so loudly. Passing a colour NAME here produces "#0a0eNaN", which the
  // browser renders as black — a silent failure that reached the live homepage
  // once already. Grey plus a console error is far easier to spot.
  if (!/^#[0-9a-f]{6}$/i.test(String(hex))) {
    console.error(`shade(): "${hex}" is not a #RRGGBB colour. ` +
      'A colour name was probably passed where a hex was expected — resolve it first.');
    return '#CCCCCC';
  }
  const [r,g,b] = hex2rgb(hex), t = amt < 0 ? 0 : 255, k = Math.abs(amt);
  return '#' + [r,g,b].map(c => clamp(c + (t - c) * k).toString(16).padStart(2,'0')).join('');
}
const lum = hex => hex2rgb(hex).map(c => c/255)
  .map(c => c <= .03928 ? c/12.92 : ((c+.055)/1.055)**2.4)
  .reduce((a,c,i) => a + c*[.2126,.7152,.0722][i], 0);
/** WCAG-style ratio, 1 (identical) → 21 (black on white). */
function contrast(a,b){ const [x,y] = [lum(a),lum(b)].sort((p,q)=>q-p); return (x+.05)/(y+.05); }
const nameOf = (hex, list) => (list.find(c => c.h.toLowerCase() === hex.toLowerCase()) || {n:hex}).n;


/* ══════════════ the keychain renderer ══════════════
   Natural left-to-right space, sized from the name. viewBox is recomputed per
   name so 3 letters and 10 letters both fill the frame.

   Two functions on purpose:
     buildKeychain() rebuilds the SVG structure — only when the NAME changes.
     paintKeychain() only sets fill/stroke on existing nodes — every colour change.
   Because paint never destroys nodes, `transition: fill` actually animates and
   the colours melt from one to the next. Rebuilding would lose that entirely. */
const CAP=78, GAP=7, PADX=13, PADY=13, THICK=11, CORDW=132, HOLE=40;
const S = (t,a,kids='') => `<${t} ${Object.entries(a).map(([k,v])=>`${k}="${v}"`).join(' ')}${kids?`>${kids}</${t}>`:'/>'}`;

function buildKeychain(svg, d, view='flat'){
  const chars = [...d.name], n = Math.max(chars.length, 1);
  const baseW = HOLE + PADX + n*CAP + (n-1)*GAP + PADX;
  const baseH = CAP + PADY*2;
  const W = CORDW + baseW, H = baseH + THICK;
  const bx = CORDW, cy = baseH/2, hx = bx + HOLE/2 + 2;
  const capsX = bx + HOLE + PADX;
  const engraved = d.finish === 'Engraved';
  const uid = svg.id || 'k';

  const defs = `<defs>
    <linearGradient id="gloss-${uid}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#fff" stop-opacity=".34"/>
      <stop offset=".55" stop-color="#fff" stop-opacity=".04"/>
      <stop offset="1" stop-color="#000" stop-opacity=".08"/>
    </linearGradient>
    <pattern id="lay-${uid}" width="4" height="4" patternUnits="userSpaceOnUse">
      <line x1="0" y1="1" x2="4" y2="1" stroke="#000" stroke-opacity=".055" stroke-width="2"/>
    </pattern>
    <filter id="drop-${uid}" x="-20%" y="-20%" width="150%" height="150%">
      <feDropShadow dx="0" dy="7" stdDeviation="7" flood-color="#1F3A5F" flood-opacity=".18"/>
    </filter>
  </defs>`;
  const gloss = `url(#gloss-${uid})`, lay = `url(#lay-${uid})`;

  // Cord loop: sweeps left from the hole and back. Drawn first so the base
  // overlaps it. No data-paint — the cord is ours to choose, so it is painted
  // once here and can never be reached by a customer's colour or a click.
  const cordD = `M ${hx} ${cy-11} C ${hx-78} ${cy-74}, 16 ${cy-62}, 16 ${cy}
                 C 16 ${cy+62}, ${hx-78} ${cy+74}, ${hx} ${cy+11}`;
  const cordAttr = {d:cordD, fill:'none', 'stroke-width':14, 'stroke-linecap':'round'};

  let g = defs + `<g filter="url(#drop-${uid})">`;
  // A ready-made clicker sold without a cord passes cord:false: no loop, no
  // knot, and the picture is cropped to the base so it does not sit off-centre.
  const corded = d.cord !== false;
  if (corded) {
  g += S('path', {...cordAttr, transform:'translate(0,6)', stroke:shade(CORD,-0.4)});
  g += S('path', {...cordAttr, stroke:CORD, 'data-cord':''});   // tap → the cord row
  // The special charm cord carries a clip set: a swivel clasp, a round bell and
  // two puffy star rings. It hangs INSIDE the cord loop, where the preview has
  // empty space anyway, so the keychain never shrinks to make room for it.
  // Flat, it clips to the top of the loop and hangs down; hanging, the loop's
  // apex is the top of the picture and the set hangs down from there.
  if (d.charm) g += view === 'hang' ? charmClipSVG(26, cy, -90) : charmClipSVG(hx/2 - 21, cy - 50, 0);
  g += S('rect', {x:hx-11, y:cy-12, width:24, height:24, rx:8, fill:shade(CORD,-0.16)});
  }

  // Base: dark thickness copy, then face, then gloss + layer lines.
  const bAttr = {x:bx, y:0, width:baseW, height:baseH, rx:18};
  g += S('rect', {...bAttr, y:THICK, 'data-paint':'base', 'data-shade':'-0.42'});
  g += S('rect', {...bAttr, 'data-paint':'base'});
  g += S('rect', {...bAttr, fill:gloss, 'pointer-events':'none'});
  g += S('rect', {...bAttr, fill:lay, 'pointer-events':'none'});
  g += S('circle', {cx:hx, cy:cy, r:11, fill:'rgba(0,0,0,.34)'});

  chars.forEach((ch, i) => {
    // A ready-made piece can colour each key on its own (cap0, letter0, …);
    // a customer's design never carries those, so every key reads cap/letter.
    const cz = d['cap'+i] ? 'cap'+i : 'cap', lz = d['letter'+i] ? 'letter'+i : 'letter';
    const x = capsX + i*(CAP+GAP), cAttr = {x, y:PADY, width:CAP, height:CAP, rx:15};
    g += S('rect', {...cAttr, y:PADY+9, 'data-paint':cz, 'data-shade':'-0.34'});
    let k = S('rect', {...cAttr, 'data-paint':cz});    // the key top, grouped so it can be pressed
    k += S('rect', {...cAttr, fill:gloss, 'pointer-events':'none'});
    k += S('rect', {...cAttr, fill:lay, 'pointer-events':'none'});
    if (ch === '♥') {
      // Fredoka has no heart, so it is drawn: same raised extrusion as a letter.
      // the printed heart: full round lobes and a soft point, filling most of the key
      const hd = 'M0 .4C-.12 .3-.56 .04-.56-.2-.56-.42-.41-.54-.26-.54-.13-.54-.04-.47 0-.37 .04-.47.13-.54.26-.54.41-.54.56-.42.56-.2.56 .04.12 .3 0 .4Z';
      const at = dy => `translate(${x+CAP/2} ${PADY+CAP/2+5+dy}) scale(${CAP*.7})`;
      k += S('path', {d:hd, transform:at(3), 'data-paint':lz, 'data-shade':'-0.45', 'pointer-events':'none'});
      k += S('path', {d:hd, transform:at(0), 'data-paint':lz});
    } else if (ch.trim() !== '') {                      // a space prints as a blank spacer cap
      const t = {x:x+CAP/2, y:PADY+CAP/2, dy:'.34em', 'text-anchor':'middle',
                 'font-family':'Fredoka, sans-serif', 'font-weight':700, 'font-size':CAP*.6};
      // Raised: dark extrusion below. Engraved: light rim above, glyph sits in the shadow.
      k += S('text', {...t, y:t.y + (engraved ? -4 : 5),
                      'data-paint': engraved ? cz : lz,
                      'data-shade': engraved ? '0.34' : '-0.45', 'pointer-events':'none'}, esc(ch));
      k += S('text', {...t, 'data-paint':lz, 'data-shade': engraved ? '-0.2' : '0'}, esc(ch));
    }
    g += `<g class="key">${k}</g>`;
  });
  g += '</g>';

  const hang = view === 'hang';
  const title = `<title>${esc(d.name || 'Your name')} keychain, ` +
                `${esc(cleanFinish(d.finish).toLowerCase())} letters, ${hang ? 'hanging' : 'flat'}</title>`;
  const x0 = corded ? -14 : CORDW - 14;               // cordless: start the frame at the base
  svg.setAttribute('viewBox', hang ? `-14 ${x0} ${H+28} ${W-x0+14}` : `${x0} -14 ${W-x0+14} ${H+28}`);
  svg.innerHTML = title + (hang ? `<g transform="translate(${H},0) rotate(90)">${g}</g>` : g);
  paintKeychain(svg, d);
}

function paintKeychain(svg, d){
  svg.querySelectorAll('[data-paint]').forEach(el => {
    el.setAttribute(el.dataset.attr || 'fill', shade(d[el.dataset.paint], +(el.dataset.shade || 0)));
  });
}

/* ══════════════ charm art ══════════════
   The clip set is drawn in a local frame hanging DOWN from its attachment point
   (0,0): split ring, swivel clasp, then a bell and two puffy star rings on short
   links. Its colours are FIXED — which set goes on is ours to choose at packing,
   so the preview shows one representative set and never follows the keychain's
   colours. `angle` turns the whole thing so "down" can follow the view. */
const CHARM_COLOUR = '#D9407A', CHARM_COLOUR_2 = '#F7B6CF';   // the dark-pink set: hot-pink clasp, bell and star, a pale-pink glass ring
const starD = (cx, cy, R, r) => Array.from({length: 10}, (_, i) => {
  const a = -Math.PI/2 + i * Math.PI/5, k = i % 2 ? r : R;
  return `${i ? 'L' : 'M'}${(cx + k*Math.cos(a)).toFixed(1)} ${(cy + k*Math.sin(a)).toFixed(1)}`;
}).join(' ') + ' Z';
function charmClipSVG(x, y, angle = 0, scale = 0.72){
  const L = CHARM_COLOUR, C = CHARM_COLOUR_2;
  const ring = {fill:'none', 'stroke-width':9, 'stroke-linejoin':'round'};
  const metal = {fill:'none', 'stroke-linecap':'round'};
  const link = (x1,y1,x2,y2) => S('line', {x1, y1, x2, y2, stroke:shade(L,-0.5), 'stroke-width':2.5});
  let g = '';
  // split ring on the cord, clasp, swivel
  g += S('circle', {cx:0, cy:0, r:9, ...metal, stroke:shade(L,-0.45), 'stroke-width':4});
  g += S('rect', {x:-8, y:8, width:16, height:36, rx:8, ...metal, stroke:L, 'stroke-width':5});
  g += S('circle', {cx:0, cy:50, r:5, fill:shade(L,-0.3)});
  // bell: a round jingle bell on a short link. Grouped so the stylesheet can
  // swing it from the link once when the charm is picked — it really rings.
  g += S('g', {class:'bell'},
         link(0, 50, -26, 68)
       + S('circle', {cx:-26, cy:80, r:12, fill:L})
       + S('path', {d:'M-36 83 Q-26 87 -16 83', fill:'none', stroke:shade(L,-0.55), 'stroke-width':2.2, 'stroke-linecap':'round'})
       + S('circle', {cx:-26, cy:88, r:1.8, fill:shade(L,-0.55)}));
  // solid star ring, then the translucent one a little lower
  g += link(0, 50, 16, 66);
  g += S('path', {d:starD(18, 92, 22, 11), ...ring, stroke:L});
  g += link(0, 50, -4, 96);
  g += S('path', {d:starD(-6, 120, 19, 9.5), ...ring, stroke:C, 'stroke-opacity':.7});
  return S('g', {transform:`translate(${x} ${y}) rotate(${angle}) scale(${scale})`, 'pointer-events':'none',
                 'data-charm':''}, g);
}
/** A cord card for the picker: a short loop, plain or with charms. */
function cordCardSVG(withClip){
  const loop = 'M78 30 C60 4,8 4,8 30 C8 56,60 56,78 30';
  const a = {d:loop, fill:'none', 'stroke-width':8, 'stroke-linecap':'round'};
  return `<svg viewBox="0 0 90 60" aria-hidden="true">${S('path', {...a, transform:'translate(0,3)', stroke:shade(CORD,-0.4)})}${S('path', {...a, stroke:CORD})}${
    withClip ? charmClipSVG(43, 11, 0, 0.3) : ''}</svg>`;
}

const clean = s => String(s).toUpperCase().replace(/[^A-Z0-9 ]/g,'').slice(0, CONFIG.maxCaps);
/* Finish is an enum, not free text. The name is normalised, the colours are
   snapped to something in stock, the quantity is clamped — finish was the one
   part of a design that arrived from the query string and reached the page
   exactly as written. Anything we do not offer becomes the default, so a stale
   link degrades to a printable design and a crafted one carries nothing. */
const cleanFinish = f => {
  const list = finishes();
  return list.find(x => x.toLowerCase() === String(f == null ? '' : f).trim().toLowerCase())
      || list[0] || 'Raised';
};
const nCapsOf = d => Math.max([...d.name].length, 1);
const priceOfDesign = d => keychainPrice(nCapsOf(d));   // minor units, Shopify's number in the theme
const BUILDER = () => PAP ? PAP.builderUrl : 'build.html';
const shareURL = d => location.origin
  + (PAP ? PAP.builderUrl : location.pathname.replace(/[^/]*$/, 'build.html'))
  + '?' + new URLSearchParams(d);
/* ══════════════ shelf art ══════════════
   Drawn, not photographed — in the real filament colours from the catalogue:
   sage, terracotta, dusty blue, black and cream. Add a product = add a doodle. */
const K='#24242A', SAGE='#9CB39A', SAGE_D='#758E78', TERRA='#C1553C', ORANGE='#E07A2E',
      BLUE='#46679B', BLUE_D='#33507C', CREAM='#F1E7D6', TEALP='#4E8F91', GOLD='#E8A33A',
      OLIVE='#4C5F42';
const ink = (w=3) => `stroke="${K}" stroke-width="${w}" stroke-linejoin="round"`;

const keycaps = (word, x0, y0, w=13, h=22) => [...word].map((c,i)=>
  `<rect x="${x0+i*(w+1)}" y="${y0}" width="${w}" height="${h}" rx="4" fill="${TEALP}" ${ink(2.5)}/>
   <text x="${x0+i*(w+1)+w/2}" y="${y0+h/2+4}" text-anchor="middle" font-family="Fredoka,sans-serif"
         font-weight="700" font-size="11" fill="${TERRA}">${c}</text>`).join('');

const PINK='#F4A6C0', LILAC='#967BB6';   // Soft Pink and Lavender, straight off the filament list
const DOODLE = {
  // The Charm Cord's product image (brand/product-card.html?id=charm): a cord
  // loop with the dark-pink clip set — clasp, bell, a solid star and a glass
  // one — in the same flat, inked style as the rest of the shelf.
  charm: `<svg viewBox="0 0 100 100">
    <path d="M50 12C26 12 10 28 10 50s16 38 40 38 40-16 40-38S74 12 50 12z" fill="none" stroke="${K}" stroke-width="9" stroke-linecap="round"/>
    <path d="M50 12C26 12 10 28 10 50s16 38 40 38 40-16 40-38S74 12 50 12z" fill="none" stroke="#3A3A44" stroke-width="3" stroke-dasharray="1 7" stroke-linecap="round"/>
    <circle cx="50" cy="13" r="5" fill="none" stroke="#D9407A" stroke-width="3"/>
    <rect x="45.5" y="17" width="9" height="16" rx="4.5" fill="#F7B6CF" ${ink(2.5)}/>
    <circle cx="50" cy="36" r="3" fill="#D9407A" ${ink(2)}/>
    <path d="M50 38l-12 9M50 38l10 8M50 38l-2 14" fill="none" stroke="${K}" stroke-width="2" stroke-linecap="round"/>
    <circle cx="36" cy="54" r="8" fill="#D9407A" ${ink(2.5)}/>
    <path d="M30 56q6 3 12 0" fill="none" stroke="${K}" stroke-width="2" stroke-linecap="round"/>
    <circle cx="36" cy="60" r="1.4" fill="${K}"/>
    <path d="M62 44l3.5 7 7.7 1-5.6 5.4 1.4 7.6-7-3.7-7 3.7 1.4-7.6-5.6-5.4 7.7-1z" fill="none" stroke="${K}" stroke-width="9.5" stroke-linejoin="round"/>
    <path d="M62 44l3.5 7 7.7 1-5.6 5.4 1.4 7.6-7-3.7-7 3.7 1.4-7.6-5.6-5.4 7.7-1z" fill="none" stroke="#D9407A" stroke-width="5.5" stroke-linejoin="round"/>
    <path d="M47 58l3.2 6.4 7 .9-5.1 4.9 1.3 7-6.4-3.4-6.4 3.4 1.3-7-5.1-4.9 7-.9z" fill="none" stroke="#F7B6CF" stroke-width="5.5" stroke-linejoin="round" opacity=".85"/>
    <path d="M47 58l3.2 6.4 7 .9-5.1 4.9 1.3 7-6.4-3.4-6.4 3.4 1.3-7-5.1-4.9 7-.9z" fill="none" stroke="${K}" stroke-width="1.5" stroke-linejoin="round"/></svg>`,
  // The Flexi Unicorn's product image (brand/product-card.html?id=unicorn). The
  // card itself comes from Shopify; this is only what the photo is drawn from.
  unicorn: `<svg viewBox="0 0 100 100">
    ${[[62,74,LILAC],[70,81,PINK],[78,87,LILAC],[86,91,PINK],[93,93,LILAC]].map(([x,y,c],i) =>
      `<ellipse cx="${x}" cy="${y}" rx="${9.5-i}" ry="${6-i*.6}" transform="rotate(35 ${x} ${y})" fill="${c}" ${ink(2.5)}/>`).join('')}
    <ellipse cx="71" cy="42" rx="6.5" ry="15" transform="rotate(-12 71 42)" fill="${LILAC}" ${ink(2.5)}/>
    <ellipse cx="67" cy="52" rx="4.5" ry="9" transform="rotate(-18 67 52)" fill="${PINK}" ${ink(2.5)}/>
    <rect x="30" y="54" width="40" height="30" rx="14" fill="${CREAM}" ${ink()}/>
    <path d="M50 74c-5-3.5-8-6-8-9.5a4 4 0 0 1 8-1a4 4 0 0 1 8 1c0 3.5-3 6-8 9.5z" fill="${PINK}" ${ink(2)}/>
    <circle cx="37" cy="84" r="6.5" fill="${CREAM}" ${ink(2.5)}/><circle cx="59" cy="84" r="6.5" fill="${CREAM}" ${ink(2.5)}/>
    <circle cx="37" cy="84" r="2.8" fill="${PINK}"/><circle cx="59" cy="84" r="2.8" fill="${PINK}"/>
    <path d="M44 22 50 2l6 20z" fill="${LILAC}" ${ink(2.5)}/>
    <path d="M47.8 9h4.4M46 15h8" stroke="${PINK}" stroke-width="2.4" stroke-linecap="round"/>
    <path d="M29 31 21 13l15 8zM71 31l8-18-15 8z" fill="${CREAM}" ${ink(2.5)}/>
    <path d="M30 27 26 18l7 4zM70 27l4-9-7 4z" fill="${PINK}"/>
    <ellipse cx="50" cy="39" rx="23" ry="21" fill="${CREAM}" ${ink()}/>
    <ellipse cx="38" cy="25" rx="9" ry="6" fill="${PINK}" ${ink(2.5)}/>
    <ellipse cx="62" cy="25" rx="9" ry="6" fill="${PINK}" ${ink(2.5)}/>
    <ellipse cx="50" cy="22" rx="9" ry="6.5" fill="${LILAC}" ${ink(2.5)}/>
    <g fill="${PINK}" opacity=".8"><ellipse cx="33" cy="47" rx="3.5" ry="2"/><ellipse cx="67" cy="47" rx="3.5" ry="2"/></g>
    <path d="M46 50.5c2 2.5 6 2.5 8 0" fill="none" ${ink(2)} stroke-linecap="round"/>
    <g fill="${K}"><circle cx="41" cy="40" r="5.5"/><circle cx="59" cy="40" r="5.5"/></g>
    <g fill="#fff"><circle cx="43" cy="38" r="2"/><circle cx="61" cy="38" r="2"/><circle cx="39.5" cy="42.5" r=".9"/><circle cx="57.5" cy="42.5" r=".9"/></g></svg>`,

  heart: `<svg viewBox="0 0 100 100">
    <path d="M50 88C20 68 10 52 10 37 10 23 20 13 32 13c8 0 14 4 18 9 4-5 10-9 18-9 12 0 22 10 22 24 0 15-10 31-40 51z" fill="${TERRA}" ${ink()}/>
    <rect x="31" y="26" width="38" height="38" rx="6" fill="${CREAM}" ${ink()}/>
    <g fill="${TERRA}" shape-rendering="crispEdges">${
      [[1,2,4,5],[0,1,2,3,4,5,6],[0,1,2,3,4,5,6],[0,1,2,3,4,5,6],[1,2,3,4,5],[2,3,4],[3]]
      .flatMap((cols,r) => cols.map(c => `<rect x="${33+c*5}" y="${29+r*5}" width="5" height="5"/>`))
      .join('')}</g>
    ${[36,50,64].map(x=>`<circle cx="${x}" cy="73" r="4" fill="${BLUE}" ${ink(2.5)}/>`).join('')}</svg>`,

  dumpling: `<svg viewBox="0 0 100 100">
    <path d="M22 62c0-19 12-31 28-31s28 12 28 31z" fill="${SAGE}" ${ink()}/>
    <g fill="none" ${ink(2.5)} stroke-linecap="round"><path d="M33 48c3-8 7-9 9-2M48 44c3-8 7-9 9-2M63 48c3-8 7-9 9-2"/></g>
    <g fill="${K}"><circle cx="42" cy="54" r="2.6"/><circle cx="58" cy="54" r="2.6"/></g>
    <path d="M46 59c2 3 6 3 8 0" fill="none" ${ink(2)} stroke-linecap="round"/>
    <rect x="16" y="61" width="68" height="17" rx="6" fill="${BLUE}" ${ink()}/>
    <g stroke="${CREAM}" stroke-width="2" opacity=".5">${[26,36,46,56,66,74].map(x=>`<path d="M${x} 64v11"/>`).join('')}</g></svg>`,

  spiral: `<svg viewBox="0 0 100 100">
    <circle cx="50" cy="11" r="4.5" fill="${K}"/><rect x="47.5" y="11" width="5" height="9" fill="${K}"/>
    <path d="M50 18c5 16 16 44 16 56 0 9-7 14-16 14s-16-5-16-14c0-12 11-40 16-56z" fill="${K}"/>
    <g fill="none" stroke="#5C5C68" stroke-width="3" stroke-linecap="round">
      <path d="M42 34c4 3 10 3 15-1"/><path d="M39 47c6 4 15 4 21-1"/>
      <path d="M36 60c8 5 19 5 27-1"/><path d="M34 73c9 6 22 6 31-1"/></g></svg>`,

  keyboard: `<svg viewBox="0 0 100 100">
    <circle cx="17" cy="50" r="12" fill="none" stroke="${SAGE_D}" stroke-width="5"/>
    <rect x="30" y="34" width="64" height="32" rx="8" fill="${SAGE_D}" ${ink()}/>
    ${keycaps('NAME', 35, 39)}</svg>`,

  names: `<svg viewBox="0 0 100 100">
    <circle cx="14" cy="50" r="10" fill="none" stroke="${SAGE_D}" stroke-width="4.5"/>
    <rect x="25" y="36" width="71" height="28" rx="7" fill="${SAGE_D}" ${ink(2.5)}/>
    ${keycaps('ZAYED', 29, 40, 12, 20)}</svg>`,

  custom: `<svg viewBox="0 0 100 100">
    <path d="M50 18 82 34 50 50 18 34Z" fill="#B7C7B4" ${ink()}/>
    <path d="M18 34v32l32 16V50Z" fill="${SAGE}" ${ink()}/>
    <path d="M82 34v32L50 82V50Z" fill="${SAGE_D}" ${ink()}/>
    <g fill="${K}"><circle cx="29" cy="52" r="2.8"/><circle cx="41" cy="58" r="2.8"/></g>
    <path d="M29 62c4 6 9 8 12 6" fill="none" ${ink(2.5)} stroke-linecap="round"/></svg>`,

  minicar: `<svg viewBox="0 0 100 100">
    <rect x="41" y="22" width="17" height="11" rx="4" fill="${SAGE_D}" ${ink()}/>
    <path d="M14 64V48h20l11-15h29l12 15v16Z" fill="${SAGE}" ${ink()}/>
    <circle cx="31" cy="67" r="11" fill="${BLUE}" ${ink()}/><circle cx="31" cy="67" r="3.5" fill="${CREAM}"/>
    <circle cx="71" cy="67" r="11" fill="${BLUE}" ${ink()}/><circle cx="71" cy="67" r="3.5" fill="${CREAM}"/></svg>`,

  balloon: `<svg viewBox="0 0 100 100">
    <ellipse cx="56" cy="24" rx="17" ry="19" fill="#EBCFA8" ${ink()}/>
    <path d="M56 43v13" ${ink(2.5)}/>
    <circle cx="30" cy="52" r="7" fill="${TERRA}" ${ink(2.5)}/>
    <path d="M16 72V60h58v12Z" fill="${SAGE}" ${ink()}/>
    <circle cx="30" cy="74" r="9" fill="${BLUE}" ${ink()}/><circle cx="66" cy="74" r="9" fill="${BLUE}" ${ink()}/></svg>`,

  flexi: `<svg viewBox="0 0 100 100">
    <g fill="${K}" ${ink(2)}>${[0,1,2,3,4].map(i=>`<path d="M${26+i*13} ${43-i*2}l6-10 6 10Z"/>`).join('')}</g>
    ${[0,1,2,3,4,5].map(i=>`<circle cx="${22+i*13}" cy="${53+Math.sin(i*.9)*6}" r="${11-i*1.1}" fill="${i?SAGE:SAGE_D}" ${ink(2.5)}/>`).join('')}
    <circle cx="18" cy="50" r="2.6" fill="${K}"/></svg>`,



  colours: `<svg viewBox="0 0 100 100">
    <rect x="24" y="24" width="52" height="52" rx="14" fill="${SAGE}" ${ink()}/>
    <text x="50" y="47" text-anchor="middle" font-family="Fredoka,sans-serif" font-weight="700" font-size="16" fill="${CREAM}">Nice</text>
    <text x="50" y="65" text-anchor="middle" font-family="Fredoka,sans-serif" font-weight="700" font-size="16" fill="${CREAM}">Day!</text></svg>`,

  party: `<svg viewBox="0 0 100 100">
    <path d="M50 16 56 34 44 32Z" fill="${GOLD}"/>
    <path d="M44 32 56 34 60 52 41 49Z" fill="${BLUE}"/>
    <path d="M41 49 60 52 64 68 37 65Z" fill="${GOLD}"/>
    <path d="M37 65 64 68 67 82 33 80Z" fill="${BLUE}"/>
    <path d="M50 16 67 82 33 80Z" fill="none" ${ink()}/>
    <circle cx="50" cy="13" r="6" fill="${TERRA}" ${ink(2.5)}/></svg>`,

  school: `<svg viewBox="0 0 100 100">
    <path d="M34 34c0-14 32-14 32 0" fill="none" stroke="${ORANGE}" stroke-width="6" stroke-linecap="round"/>
    <rect x="20" y="26" width="60" height="56" rx="14" fill="${BLUE}" ${ink()}/>
    <path d="M50 34l7 10-7 10-7-10z" fill="${BLUE_D}"/>
    <rect x="28" y="56" width="44" height="26" rx="8" fill="${ORANGE}" ${ink()}/>
    <rect x="36" y="63" width="28" height="12" rx="3" fill="${CREAM}" ${ink(2)}/></svg>`,

  business: `<svg viewBox="0 0 100 100">
    <rect x="22" y="44" width="56" height="38" rx="5" fill="${OLIVE}" ${ink()}/>
    <rect x="17" y="34" width="66" height="14" rx="5" fill="#5D7253" ${ink()}/>
    <rect x="44" y="34" width="12" height="48" fill="${ORANGE}" ${ink(2.5)}/>
    <path d="M50 34C38 34 32 26 38 21s14 4 12 13c-2-9 6-18 12-13s0 13-12 13z" fill="${ORANGE}" ${ink(2.5)}/></svg>`,

  oneoff: `<svg viewBox="0 0 100 100">
    <g stroke="${K}" stroke-width="3" stroke-linecap="round">
      <path d="M50 8v7M26 18l5 5M74 18l-5 5M18 42h7M82 42h-7"/></g>
    <path d="M50 20c13 0 21 10 21 20 0 9-6 12-8 18H37c-2-6-8-9-8-18 0-10 8-20 21-20z" fill="${GOLD}" ${ink()}/>
    <path d="M44 54c1-9 11-9 12 0" fill="none" ${ink(2.5)}/>
    <rect x="38" y="58" width="24" height="9" rx="2" fill="#9A9AA2" ${ink(2.5)}/>
    <rect x="41" y="69" width="18" height="8" rx="4" fill="#70707A" ${ink(2.5)}/></svg>`
};

/* ══════════════ cart ══════════════
   localStorage, so it survives the hop between the shop and the builder. */
let cart = JSON.parse(localStorage.getItem('pap.cart.v2') || '[]');   // v2: prices in minor units
const save = () => localStorage.setItem('pap.cart.v2', JSON.stringify(cart));

/* Shopify's word on how many can actually be sold: null = untracked, no
   limit; 0 = sold out. Only shelf products carry stock — configured
   keychains are made to order and never run out. */
const stockCap = p => !p ? null : p.av === false ? 0 : (p.q != null ? p.q : null);

/* Shelf "Add" works on any page that renders the shelf. */
function wireShelf(el){
  el.addEventListener('click', e => {
    const b = e.target.closest('[data-add]'); if (!b) return;
    const p = SHELF.find(s => s.id === b.dataset.add) || CONFIG.shelf.find(s => s.id === b.dataset.add);
    if (!p) return;
    const ex = cart.find(x => x.kind === 'shelf' && x.id === p.id);
    const price = p.price != null ? p.price : shelfPrice(p);
    if (price == null) { toast(`${p.n} is not on sale yet`); return; }
    const cap = stockCap(p);
    if (cap === 0) { toast(`${p.n} is sold out`); return; }
    if (cap != null && ex && ex.qty >= cap) {
      toast(`Only ${cap} in stock — they're all in your cart`); openCart(true); return;
    }
    /* Shelf intent, with the same clean semantics as pap_builder_add_attempt:
       an ELIGIBLE Add that was expected to proceed — it fires only past every
       guard above, so comparing it against product_added_to_cart measures real
       add-to-cart failures, not sold-out taps. (Demand for unavailable
       products would be its own event, e.g. pap_shelf_sold_out_clicked — not
       implemented.) No product text rides along; device_class is injected by
       track() itself. */
    track('pap_shelf_add_attempt', {});
    if (ex) ex.qty++;
    else cart.push({kind:'shelf', id:p.id, n:p.n, price, qty:1, variant:p.variant, img:p.img});
    save(); renderCart(); openCart(true);
  });
}

/** What one of a line costs: the keychain variant plus its charm cord, if any.
    Looked up live so a repriced cord never leaves a stale number in a basket. */
const lineUnit = i => i.price + (i.kind === 'name' ? charmCost(i.design) : 0);
const subtotal = () => cart.reduce((s,i) => s + lineUnit(i) * i.qty, 0);

function renderCart(){
  // Not every page has the drawer (Shopify's own cart/product templates, the
  // build-time share-image card). Wiring is optional; the engine still works.
  if (!$('dbd')) return;
  renderDeals(); renderUpsell();
  $('cartN').textContent = cart.reduce((s,i) => s + i.qty, 0);
  $('dbd').innerHTML = cart.length ? cart.map((i,idx) => {
    const meta = i.kind === 'name'
      ? `${i.caps} keycaps · ${esc(cleanFinish(i.design.finish))}<br>
         Letter ${nameOf(i.design.letter,CONFIG.filaments)} · Keycap ${nameOf(i.design.cap,CONFIG.filaments)}<br>
         Base ${nameOf(i.design.base,CONFIG.filaments)}${i.design.charm
           ? ` · <b>${CHARM.n}</b>` : ''}${lineStale(i)
           ? `<span class="stale">&#9888; ${esc(staleNames(i.design))} is out of stock — tap Edit to pick another</span>` : ''}`
      : 'Ready to print';
    return `<div class="li">
      <div class="th">${i.kind==='name' ? thumb(i.design) : lineArt(i)}</div>
      <div class="li-main">
        <div class="nm">${i.kind==='name' ? esc(i.design.name) : esc(i.n)}</div>
        <div class="mt">${meta}</div>
        <div class="li-foot">
          <div class="qty qty-sm">
            <button data-q="${idx}" data-d="-1" aria-label="One fewer ${esc(i.n || i.design.name)}">&minus;</button>
            <span aria-live="polite">${i.qty}</span>
            <button data-q="${idx}" data-d="1" aria-label="One more ${esc(i.n || i.design.name)}">+</button>
          </div>
          <b>${money(lineUnit(i) * i.qty)}</b>
        </div>
      </div>
      <div class="acts">
        ${i.kind === 'name' ? `<a class="rm" href="${editHref(i)}">Edit</a>` : ''}
        <button class="rm" data-rm="${idx}">Remove</button>
      </div></div>`;
  }).join('') : '<p class="empty">Nothing here yet.<br>Go build a name.</p>';

  const gross = subtotal();
  const kc = keychainCount(), saved = bundleSaving(kc), nxt = nextTier(kc);
  const net = gross - saved;

  // Show what they will actually pay, with the pre-discount figure struck
  // through. Shopify still does the arithmetic that counts — this only mirrors
  // the tiers configured in theme settings, which must match the automatic
  // discount in admin.
  $('sub').innerHTML = saved
    ? `<s>${money(gross)}</s> <b>${money(net)}</b>`
    : money(gross);

  const bd = $('bundle');
  if (bd) {
    bd.innerHTML = saved
      ? `<span class="save">Bundle deal &minus;${money(saved)}</span>
         <span class="at">applied at checkout</span>`
      : (nxt && kc ? `<span class="at">Add ${nxt.qty - kc} more design${nxt.qty - kc > 1 ? 's' : ''}
           and save ${money(tierSaving(nxt, keychainPrice(1) || 0))}</span>` : '');
    bd.hidden = !bd.innerHTML.trim();
  }

  const busy = $('busy'), note = (PAP ? PAP.busy : CONFIG.busyNote) || '';
  if (busy) {
    busy.innerHTML = note.trim()
      ? `<b>&#10024; Made just for you</b><br>${esc(note.trim())}` : '';
    busy.hidden = !cart.length || !note.trim();   // nothing to wait for on an empty cart
  }
  $('checkout').disabled = !cart.length || cart.some(lineStale);
  $('dev').innerHTML = cart.length ? devNote() : '';
}
$('dbd').addEventListener('click', e => {
  const q = e.target.closest('[data-q]');
  if (q) {
    const i = +q.dataset.q;
    let next = Math.min(99, cart[i].qty + (+q.dataset.d));
    // the + button stops at Shopify's stock, with a word, not a checkout error
    if (cart[i].kind === 'shelf' && next > cart[i].qty) {
      const cap = stockCap(SHELF.find(s => s.id === cart[i].id));
      if (cap != null && next > cap) {
        next = Math.max(1, Math.min(next - 1, cap));   // hold, never remove, on a plus
        toast(cap === 0 ? 'Sold out — this one may not go through' : `Only ${cap} in stock`);
      }
    }
    // a keychain with a charm cord is capped by the cords left, counting every
    // other charm-cord line in the basket
    if (cart[i].kind === 'name' && cart[i].design.charm && next > cart[i].qty) {
      const room = charmRoom(charmStock(), charmInCart(cart[i].uid));
      if (next > room) {
        next = Math.max(1, Math.min(next - 1, room));
        toast(room === 0 ? 'No special charms left — this one may not go through'
                         : 'Not enough special charms left for that many');
      }
    }
    if (next < 1) cart.splice(i, 1);          // stepping below one removes the line
    else cart[i].qty = Math.max(1, next);
    save(); renderCart(); return;
  }
  const b = e.target.closest('[data-rm]'); if (!b) return;
  cart.splice(+b.dataset.rm, 1); save(); renderCart();
});

/** Two keychains are the same line only if every part of the print matches. */
const DESIGN_KEYS = ['name','letter','cap','base','finish','charm'];
const sameDesign = (a, b) => DESIGN_KEYS.every(k => a[k] === b[k]);
const newUid = () => Math.random().toString(36).slice(2, 8);

/** Art for a cart line: the product photo when Shopify has one, the drawing
    otherwise, and a plain box for anything older than both. */
function lineArt(i){
  if (i.img) return `<img src="${i.img}" alt="">`;
  if (DOODLE[i.id]) return DOODLE[i.id];
  return `<svg viewBox="0 0 60 60"><rect x="12" y="18" width="36" height="28" rx="6"
    fill="none" stroke="#5B6B80" stroke-width="3"/><path d="M12 26h36" stroke="#5B6B80"
    stroke-width="3"/><path d="M26 18v8" stroke="#5B6B80" stroke-width="3"/></svg>`;
}

/** Tiny standalone render of a design, for the cart line. */
function thumb(d){
  return `<svg viewBox="0 0 60 60"><rect x="4" y="16" width="52" height="28" rx="7" fill="${shade(d.base,-.42)}" transform="translate(0,3)"/>
    <rect x="4" y="16" width="52" height="28" rx="7" fill="${d.base}"/>
    <rect x="14" y="21" width="18" height="18" rx="5" fill="${d.cap}"/>
    <rect x="35" y="21" width="18" height="18" rx="5" fill="${d.cap}"/>
    <circle cx="23" cy="30" r="4.5" fill="${d.letter}"/><circle cx="44" cy="30" r="4.5" fill="${d.letter}"/>
    <path d="M9 30 C -2 18, -2 42, 9 30" fill="none" stroke="${CORD}" stroke-width="4"/></svg>`;
}

/* ══════════════ checkout ══════════════ */

/** Reopen a saved design in the builder, flagged so Add becomes Update. */
function editHref(i){
  return BUILDER() + '?' + new URLSearchParams({...i.design, q: i.qty, edit: i.uid || ''});
}

/** What the customer sees on the order, plus two hidden fields for you.
    `_`-prefixed properties are hidden from the customer but visible on the order. */
const designProps = d => {
  // A basket saved before this fix can still hold a finish nobody offers, so
  // normalise on the way out too — the order, the print spec and the reopen
  // link must all name something we actually print.
  const spec = {...d, finish: cleanFinish(d.finish)};
  if (!spec.charm) delete spec.charm;      // absent, never empty — see CHARM
  return {
    'Name'  : spec.name,
    'Letter': nameOf(spec.letter, CONFIG.filaments),
    'Keycap': nameOf(spec.cap,    CONFIG.filaments),
    'Base'  : nameOf(spec.base,   CONFIG.filaments),
    // A fixed line, not a field: it tells the packer to choose and tells the
    // customer why no cord colour appears on their confirmation.
    // Both read as a promise: colour, and for the special cord the charms too,
    // are chosen by us at packing. The customer never picks a charm.
    'Cord'  : spec.charm ? 'Special charm cord — charms chosen by Print A Pal'
                         : 'Chosen by Print A Pal to match',
    'Finish': spec.finish,
    '_build': JSON.stringify(spec),   // machine-readable spec for the print queue
    '_link' : shareURL(spec)          // reopens the exact design in the builder
  };
};

/* Static build: a cart permalink. No API token, nothing secret in the repo.
   Ceiling: permalinks carry CART-level attributes, not per-line ones, so only
   one configured keychain fits per checkout. The theme path below has no such
   limit — that is the reason it exists. */
function checkoutURL(){
  const lines = cart.flatMap(i => [`${variantOf(i)}:${i.qty}`,
    ...(i.kind === 'name' && i.design.charm && CONFIG.charmVariantId
        ? [`${CONFIG.charmVariantId}:${i.qty}`] : [])]).join(',');
  const p = new URLSearchParams();
  const d = cart.find(i => i.kind === 'name');
  if (d) Object.entries(designProps(d.design))
          .forEach(([k,v]) => p.append(`attributes[${k}]`, v));
  return `https://${CONFIG.shop}/cart/${lines}?${p}`;
}
const variantOf = i => i.kind === 'name' ? CONFIG.variants[i.caps]
                                        : i.variant || (CONFIG.shelf.find(s => s.id === i.id) || {}).variantId;
const ready = () => CONFIG.shop.startsWith('your-store') ? false : cart.every(i => variantOf(i));

/* Theme build: the store's own AJAX cart. Properties attach per LINE, so two
   different names in one order are fine. */
async function themeCheckout(){
  const items = [], missing = [];
  for (const i of cart) {
    if (i.kind !== 'name') {
      const id = i.variant || (PAP.shelf[i.id]||{}).v;
      if (!id) missing.push(i.n);
      items.push({ id, quantity: i.qty });
      continue;
    }
    const id = (PAP.keychain[i.caps]||{}).v;
    if (!id) missing.push(`${i.caps}-keycap keychain`);
    items.push({ id, quantity: i.qty, properties: designProps(i.design) });
    // The charm cord is its own line, so Shopify charges it and the packer sees
    // it. "For" names the keychain it belongs on; the keychain's own Cord
    // property says the same, so the pairing reads from either side.
    if (i.design.charm) {
      const c = PAP.charm;
      if (!c || !c.v) missing.push('the special charm cord');
      items.push({ id: c && c.v, quantity: i.qty, properties: { 'For': i.design.name } });
    }
  }
  if (missing.length) { toast('Not published in Shopify yet: ' + missing.join(', ')); return; }

  if (cart.some(lineStale)) {
    toast('A colour in your basket is out of stock — tap Edit on that design to pick another');
    openCart(true); renderCart(); return;
  }
  const btn = $('checkout'); btn.disabled = true; btn.textContent = 'Adding…';
  try {
    // Shopify's cart lives on the server and survives everything we do here, so
    // adding to it would stack on top of an abandoned basket the customer can no
    // longer see or remove. Wipe it first: this drawer is the source of truth.
    const cleared = await fetch('/cart/clear.js', { method:'POST',
      headers:{'Content-Type':'application/json'} });
    if (!cleared.ok) throw new Error('could not clear the previous basket');

    const r = await fetch('/cart/add.js', { method:'POST',
      headers:{'Content-Type':'application/json'}, body: JSON.stringify({items}) });
    if (!r.ok) throw new Error((await r.json().catch(()=>({}))).description || r.status);
    // Keep the drawer. Emptying it here meant that pressing Back from checkout
    // looked like the basket had vanished. Re-checkout is safe because we clear
    // Shopify's cart first, so handing over twice is idempotent.
    localStorage.setItem('pap.handover', '1');
    location.href = '/checkout';
  } catch (e) {
    btn.disabled = false; btn.textContent = 'Checkout';
    toast('Shopify would not take that: ' + e.message);
  }
}

if ($('checkout')) $('checkout').onclick = () => {
  if (PAP) return themeCheckout();
  if (cart.some(lineStale)) { toast('A colour in your basket is out of stock — tap Edit on that design to pick another'); renderCart(); return; }
  if (!ready()) { toast('Demo mode — add your shop domain and variant IDs in CONFIG'); return; }
  location.href = checkoutURL();
};
function devNote(){
  if (PAP) return '';                     // live on the store: nothing to explain
  return ready()
    ? `Checkout goes to <code>${CONFIG.shop}</code>.`
    : `<b>Demo mode.</b> Fill <code>CONFIG.shop</code> and <code>CONFIG.variants</code> to go live.
       This cart would open:<br><code>${esc(checkoutURL())}</code>`;
}

/* ══════════════ payment badges ══════════════
   In the theme these are Shopify's own licensed logos for the methods actually
   enabled on the store, so the row can never advertise a method you don't take.
   The static demo has no store to ask, so it falls back to plain labels rather
   than inventing brand marks. */
const PAY_ORDER = ['apple_pay','google_pay','visa','tabby','master','american_express',
                   'paypal','shopify_pay','amex','mada','discover'];
const PAY_LABEL = {apple_pay:'Apple Pay', google_pay:'Google Pay', visa:'Visa', tabby:'Tabby',
                   master:'Mastercard', american_express:'Amex', amex:'Amex',
                   paypal:'PayPal', shopify_pay:'Shop Pay', mada:'mada', discover:'Discover'};
const PAY_MAX = 6;
/* Tabby is taken at checkout but is not one of Shopify's payment_types, so its
   badge is ours: assets/tabby.svg, served from the theme. */
const TABBY = () => ({ t:'tabby', src: PAP ? PAP.tabby : 'assets/tabby.svg' });

function paymentsHTML(){
  const rank = t => { const i = PAY_ORDER.indexOf(t); return i < 0 ? 99 : i; };
  let row;
  if (PAP && PAP.payments && PAP.payments.length) {
    const list = PAP.payments.some(p => p.t === 'tabby') ? PAP.payments : [...PAP.payments, TABBY()];
    row = list.filter(p => p.src).sort((a,b) => rank(a.t) - rank(b.t)).slice(0, PAY_MAX)
      .map(p => {
        const label = PAY_LABEL[p.t] || p.t;
        // If Shopify ever moves the logo, show the name rather than a broken image.
        return `<img src="${p.src}" alt="${label}" loading="lazy" width="38" height="24"
                 onerror="this.outerHTML='<span>${label}</span>'">`;
      });
  } else {
    row = ['apple_pay','google_pay','visa','tabby','master'].map(t => t === 'tabby'
      ? `<img src="${TABBY().src}" alt="Tabby" width="38" height="24">` : `<span>${PAY_LABEL[t]}</span>`);
  }
  const cod = PAP ? PAP.cod : 'Cash on delivery';
  if (cod && row.length < PAY_MAX) row.push(`<span>${esc(cod)}</span>`);
  return `<div class="paylist" aria-label="Accepted payment methods">${row.join('')}</div>`;
}
/** The deal, as pills. The tier matching what is already in the basket is
    marked, so the offer reads as progress rather than an advert. */
function renderDeals(){
  const rows = bundleHeadline();
  document.querySelectorAll('[data-deals]').forEach(el => {
    if (rows.length < 2) { el.innerHTML = ''; return; }
    const have = keychainCount();
    let best = 1;
    for (const r of rows) if (have >= r.qty) best = r.qty;
    el.innerHTML = `<div class="deals">${rows.map(r => `
      <span class="deal${r.qty === best && have ? ' on' : ''}">
        <b>${r.qty}</b> ${dealWord()} ${money(r.price)}
        ${r.save ? `<i>save ${money(r.save)}</i>` : ''}
      </span>`).join('')}</div>${dealUnit != null
        ? '<p class="dealnote">if each one is like this design</p>' : ''}`;
  });
}

/** One line of encouragement under the price: how close they are to the next
    tier, or that they have already earned one. */
function renderUpsell(){
  const el = $('upsell'); if (!el) return;
  const have = keychainCount(), next = nextTier(have), got = bundleSaving(have);
  if (!TIERS.length || keychainPrice(1) == null) { el.hidden = true; return; }
  el.hidden = false;
  const unit = dealUnit != null ? dealUnit : keychainPrice(1);
  if (next) {
    const more = next.qty - have;
    const nextSave = tierSaving(next, unit);
    const at = money(unit * next.qty - nextSave);
    el.innerHTML = have === 0
      ? `<b>Make another &amp; save ✨</b> ${next.qty} like this comes to ${at}.`
      : `<b>${more} more and you save ${money(Math.max(0, nextSave - got))}</b> — ${next.qty} like this comes to ${at}.`;
  } else {
    el.innerHTML = `<b>Best deal unlocked 🎉</b> ${money(got)} comes off at checkout.`;
  }
}

function renderPayments(){
  document.querySelectorAll('[data-payments]').forEach(el => el.innerHTML = paymentsHTML());
}

/* Did they buy, or just come back?

   Shopify empties its own cart once an order is placed, so an empty cart after
   a handover means the order went through and ours should follow. A cart that
   still has items means they changed their mind, and the drawer stays as they
   left it. */
async function reconcileWithShopify(){
  if (!PAP || !localStorage.getItem('pap.handover')) return;
  try {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (r.ok && (await r.json()).item_count === 0) { cart = []; save(); }
  } catch { /* offline or blocked — leave the drawer alone */ }
  localStorage.removeItem('pap.handover');
  renderCart();
}

/* ══════════════ drawer + toast ══════════════ */
const openCart = on => {
  if (!$('drawer')) return;
  $('drawer').classList.toggle('on', on); $('scrim').classList.toggle('on', on);
  $('drawer').setAttribute('aria-hidden', !on);
  if (on) $('close').focus();
  placeToast();
};
if ($('cartBtn')) $('cartBtn').onclick = () => openCart(true);
if ($('close'))   $('close').onclick   = () => openCart(false);
if ($('scrim'))   $('scrim').onclick   = () => openCart(false);
addEventListener('keydown', e => e.key === 'Escape' && $('drawer') && openCart(false));

let toastT;
/* The drawer's footer holds the total, the Checkout button and the payment
   badges — exactly what people read after adding something, and exactly what a
   bottom-anchored toast used to cover for three seconds. So while the drawer is
   open the toast sits just above that footer instead of on top of it. Measured
   rather than guessed: the footer grows with the bundle and busy lines. */
function placeToast(){
  const t = $('toast'); if (!t) return;
  const dr = $('drawer'), ft = document.querySelector('.dft');
  const overFooter = dr && ft && dr.classList.contains('on');
  // offsetHeight, not a measured rect: this runs while the drawer is still
  // sliding in, and a transformed element reports where it is, not where it lands.
  t.style.bottom = overFooter ? `${ft.offsetHeight + 16}px` : '26px';
}
function toast(msg){
  let t = $('toast');
  if (!t) { t = document.createElement('div'); t.id = 'toast'; document.body.append(t);
    t.style.cssText = 'position:fixed;left:50%;bottom:26px;transform:translateX(-50%);z-index:70;background:#1F3A5F;'+
      'color:#fff;padding:12px 18px;border-radius:14px;font-size:14px;transition:opacity .2s;'+
      'box-shadow:0 6px 20px rgba(0,0,0,.25);max-width:min(92vw,420px);text-align:center';
  }
  t.textContent = msg; t.style.opacity = 1;
  placeToast();
  clearTimeout(toastT); toastT = setTimeout(() => t.style.opacity = 0, 2600);
}


/* The catalogue comes from Shopify when we are in the theme.

   Sections are each product's Product type, so the shop is arranged in admin.
   Set a product to Unlisted, Draft or Archived and it leaves the site by
   itself — collections exclude all three, and collections are what the
   catalogue snippet iterates.

   The static demo has no store to ask, so it falls back to CONFIG.shelf. */
function catalogueItems(){
  if (!PAP || !PAP.catalogue) return CONFIG.shelf.filter(isOffered);

  // The charm cord is sold through the builder, never as a shelf card — even
  // if it is left listed in admin.
  // Nor is the Flag Day keychain a shelf card: it has its own band and page.
  const items = PAP.catalogue.filter(p => p.h !== PAP.charmHandle && p.h !== PAP.flagHandle).map(p => ({
    id: p.h, n: p.t, sub: p.d, tag: p.ty, img: p.img,
    price: p.p, variant: p.v,
    // Shopify's word on stock: av false = sold out, q = tracked units left
    // (null = untracked, no limit). Older cached snippets simply omit both.
    av: p.av !== false, q: typeof p.q === 'number' ? p.q : null,
    ld: p.ld === true,          // tagged "limited drop" in Shopify → badge on the card
    // the configurable product sends people to the builder instead of the cart
    href: p.h === PAP.keychainHandle ? BUILDER() : undefined
  }));

  // Enquiry-only cards have no Shopify product, so they are opt-in.
  if (PAP.showEnquiry) items.push(...CONFIG.shelf.filter(p => p.ask));
  return items;
}

/** Sections in a sensible order: the ones we know about first, then whatever
    other Product types the shop happens to use. */
function catalogueSections(items){
  const known = CONFIG.categories.map(c => c.n);
  const seen = [...new Set(items.map(i => i.tag))];
  const order = [...known.filter(n => seen.includes(n)),
                 ...seen.filter(n => !known.includes(n)).sort()];
  return order.map(n => ({
    n, sub: (CONFIG.categories.find(c => c.n === n) || {}).sub || '',
    items: items.filter(i => i.tag === n)
  })).filter(s => s.items.length);
}

/* Only show what the shop can actually sell.

   The shelf used to render straight from CONFIG, so a card appeared whether or
   not Shopify had the product — unpublish something in admin and it kept
   selling here. Now the theme decides: no product, no card, no code change.

   The static demo has no store to ask, so it shows everything. */
function isOffered(p){
  if (!PAP) return true;
  if (p.ask)  return !!PAP.showEnquiry;                       // no product behind these at all
  if (p.href) return Object.keys(PAP.keychain || {}).length > 0;
  return !!PAP.shelf[p.id];
}

const shelfCard = p => {
  const cap = stockCap(p);
  const out = cap === 0, low = !out && cap != null && cap <= 5;
  return `
  <article class="card">
    <div class="art">${p.img
      ? `<img src="${p.img}" alt="${esc(p.n)}" loading="lazy" decoding="async">`
      : (DOODLE[p.id] || '')}</div>
    <div class="bd">
      <h3>${p.n}</h3>
      <p class="sub">${p.sub}</p>
      ${p.ld || out || low ? `<div class="chips">
        ${p.ld ? `<span class="stockchip drop">&#10024; Limited drop${!out && cap != null ? ` &middot; only ${cap} left` : ''}</span>` : ''}
        ${out ? `<span class="stockchip out">Sold out — more printing soon</span>`
          : low && !p.ld ? `<span class="stockchip">Only ${cap} left</span>` : ''}</div>` : ''}
      <div class="ft">
        <span class="p${p.href || p.ask || shelfPrice(p) == null ? ' from' : ''}">${
          p.href ? (fromPrice() == null ? 'Made to order' : `from ${money(fromPrice())}`)
                 : p.ask ? 'Made to order'
                 : money(p.price != null ? p.price : shelfPrice(p))}</span>
        ${p.href ? `<a class="btn sm" href="${BUILDER()}">Build yours</a>`
          : p.ask ? `<a class="btn sm" href="${CONFIG.contact}" target="_blank" rel="noopener">Ask us</a>`
          : out ? `<button class="btn sm" disabled>Sold out</button>`
          : `<button class="btn sm" data-add="${p.id}">Add</button>`}
      </div>
    </div>
  </article>`;
};

/* Grouped by your own three shelves rather than one flat grid. */
let SHELF = [];
function renderShelf(el){
  SHELF = catalogueItems();
  const secs = catalogueSections(SHELF);
  el.innerHTML = secs.length ? secs.map(s => `<section class="cat">
      <div class="cathd"><h3>${esc(s.n)}</h3>${s.sub ? `<p>${esc(s.sub)}</p>` : ''}</div>
      <div class="grid">${s.items.map(shelfCard).join('')}</div>
    </section>`).join('')
    : `<p class="empty">Nothing is listed yet. Set a product to Active in Shopify and it appears here.</p>`;
  wireShelf(el);
}

/* ══════════════ analytics ══════════════
   Builder events go through Shopify.analytics.publish into the Web Pixels
   event bus, where Shopify's customer-privacy machinery gates them and a
   pixel (Shopify admin → Settings → Customer events) forwards them to GA4 or
   wherever. On a page without Shopify — the static Vercel build — there is no
   bus and the event goes nowhere, silently. Deliberately NOT emitted here:
   add_to_cart, begin_checkout, purchase — Shopify publishes those as standard
   pixel events itself, and a second copy would double-count the funnel.

   Event names AND properties are allowlisted, which is what stops a
   customer's name riding along on an otherwise innocent event. The name never
   leaves the device; name_length does. See
   docs/printapal_experience_v4_2_final_control_pack/20_ANALYTICS_EVENTS.md */
const TRACK_EVENTS = [
  'pap_builder_view', 'pap_builder_start', 'pap_builder_ready', 'pap_builder_add_attempt',
  'pap_color_changed', 'pap_palette_selected', 'pap_finish_changed', 'pap_quantity_changed',
  'pap_share_clicked', 'pap_make_another_clicked', 'pap_shelf_add_attempt'
];
const TRACK_PROPS = ['name_length', 'zone', 'palette_id', 'finish_id', 'quantity',
                     'device_class', 'entry_source', 'traffic_source'];

/* Coarse device class from the layout's OWN breakpoints (app.css collapses at
   880px, phones at 620px) — never a user agent, never exact dimensions. Read
   once: a class that changes mid-session from a window drag is noise. */
const DEVICE_CLASS = (typeof matchMedia === 'function')
  ? (matchMedia('(max-width:620px)').matches ? 'mobile'
   : matchMedia('(max-width:880px)').matches ? 'tablet' : 'desktop')
  : 'desktop';

/* Acquisition, classified ONCE per browser session and stored as the enum
   value only. Raw URLs, query strings, referrers, utm values and click ids
   are read here to pick a bucket and then dropped — none is ever sent or
   stored. TRUTHFUL buckets: a click id alone does not prove paid traffic.
   Precedence order:
     meta_paid          explicit paid utm_medium (paid/cpc/ppc/ad) on a Meta
                        utm_source — the only thing that may claim "paid Meta"
     google_paid        gclid (Google appends it to ad clicks), or an explicit
                        Google paid utm
     meta_other         Meta click evidence without a paid marker: fbclid, a
                        facebook.com referrer, or an unpaid Meta utm_source
     instagram_organic  an instagram.com referrer with no click id, no paid utm
     google_organic     a google.* referrer or unpaid utm_source=google
     direct             no referrer and no campaign evidence at session start
     other              everything else, including a same-origin first touch */
const TRAFFIC_SOURCE = (() => {
  try {
    const stored = sessionStorage.getItem('pap_traffic');
    if (stored) return stored;
    const q = new URLSearchParams(location.search);
    const utmS = (q.get('utm_source') || '').toLowerCase();
    const utmM = (q.get('utm_medium') || '').toLowerCase();
    let ref = '';
    try { ref = new URL(document.referrer).hostname; } catch (_) {}
    const metaSrc = ['facebook', 'fb', 'meta', 'instagram', 'ig'].includes(utmS);
    const paid = /paid|cpc|ppc|(^|[_\-.])ads?([_\-.]|$)/.test(utmM);
    const instaRef = /(^|\.)instagram\.com$/.test(ref);
    const fbRef = /(^|\.)(facebook|fb)\.com$/.test(ref);
    const googleRef = /(^|\.)google\.[a-z.]+$/.test(ref);
    let t = 'other';
    if (metaSrc && paid) t = 'meta_paid';
    else if (q.get('gclid') != null || (utmS === 'google' && paid)) t = 'google_paid';
    else if (q.get('fbclid') != null) t = 'meta_other';       // click evidence, unproven paid
    else if (instaRef) t = 'instagram_organic';   // an unpaid utm on a bio link stays organic
    else if (fbRef || metaSrc) t = 'meta_other';
    else if (googleRef || utmS === 'google') t = 'google_organic';
    else if (!ref && !utmS && !utmM) t = 'direct';
    sessionStorage.setItem('pap_traffic', t);
    return t;
  } catch (_) { return 'other'; }
})();
const defaultSink = (event, props) => {
  // ?test runs fire deliberate junk that must never reach a real pixel
  if (/[?&]test\b/.test(location.search)) return;
  const a = window.Shopify && window.Shopify.analytics;
  if (a && typeof a.publish === 'function') a.publish(event, props);
};
let sink = defaultSink;
function track(event, props) {
  if (!TRACK_EVENTS.includes(event)) return;
  const safe = { device_class: DEVICE_CLASS };   // every event is device-segmented
  for (const k of Object.keys(props || {})) if (TRACK_PROPS.includes(k)) safe[k] = props[k];
  try { sink(event, safe); } catch (_) { /* analytics must never break commerce */ }
}
/* setSink(fn) lets a test capture events; setSink(null) hands back the real one. */
const setSink = fn => { sink = typeof fn === 'function' ? fn : defaultSink; };

/* ══════════════ boot (shared) ══════════════ */
// In the theme, the merchant edits these in the theme editor, not in this file.
if (PAP) {
  if (PAP.contact)  CONFIG.contact = PAP.contact;
}
if (PAP) CONFIG.filaments = parsePalette(PAP.filaments) || CONFIG.filaments;
TIERS = parseTiers(PAP && PAP.bundleTiers != null ? PAP.bundleTiers : CONFIG.bundleTiers);
OOS = { filament: asSet(PAP ? PAP.oosFilament : CONFIG.outOfStockFilament) };
document.querySelectorAll('[data-max]').forEach(e => e.textContent = CONFIG.maxCaps);
renderPayments();
renderDeals();
renderCart();
reconcileWithShopify();

/* ══════════════ the Flag Day edition ══════════════
   A ready-made keychain — U A E ♥ in the flag's colours — sold as one Shopify
   product, not through the builder. Nothing to choose, so the keys carry their
   own colours (cap0/letter0…) instead of the three zones. In the theme its
   variant and price come from PAP.flag (null = not published → every flag
   block stays hidden); the static demo falls back to CONFIG.flagPrice.

   Any page can carry it: [data-flag-section] unhides, [data-flag-art] svgs
   draw it, [data-flag-price] / [data-flag-days] fill in, [data-flag-add] adds
   it to the basket and [data-flag-href] links to its page. */
const FLAG_ID = PAP ? PAP.flagHandle : 'uae-flag-clicker';
// the key, then its keycap and letter filament — written in the order printed
const FLAG_KEYS = [['U','Red','White'], ['A','Green','White'], ['E','Black','White'], ['♥','White','Red']];
const FLAG_HEX = {Red:'#FF0000', Green:'#4CA64C', Black:'#2E2E33', White:'#FFFFFF'};
const flagHex = n => hexOf(n) || FLAG_HEX[n];
const flagDesign = () => FLAG_KEYS.reduce((d, [, cap, letter], i) =>
  ({...d, ['cap'+i]: flagHex(cap), ['letter'+i]: flagHex(letter)}),
  {name: FLAG_KEYS.map(k => k[0]).join(''), cord: false, base: flagHex('White'), cap: '#FFFFFF', letter: '#FFFFFF', finish: 'Raised'});
const flagProduct = () => PAP ? PAP.flag
  : !CONFIG.flagLive ? null : {v: null, p: CONFIG.flagPrice * 100, av: true, t: 'UAE Flag Day Clicker', img: null};

/** Days from today to 3 November, Flag Day. Negative once it has passed. */
function flagDays(now = new Date()){
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((new Date(now.getFullYear(), 10, 3) - today) / 864e5);
}
const flagDaysText = n => n > 1 ? `${n} days to Flag Day` : n === 1 ? 'Flag Day is tomorrow'
                        : n === 0 ? 'Happy Flag Day!' : 'Flag Day edition';

(function mountFlag(){
  const fp = flagProduct();
  if (!fp || !fp.v && PAP) return;                    // not published: stay hidden
  const d = flagDesign();
  DOODLE[FLAG_ID] = `<svg viewBox="0 0 60 60"><rect x="3" y="20" width="54" height="22" rx="6" fill="${shade(d.base,-.3)}" transform="translate(0,3)"/>
    <rect x="3" y="20" width="54" height="22" rx="6" fill="${d.base}" stroke="#E3D8C4"/>${FLAG_KEYS.map((_, i) =>
    `<rect x="${7+i*12.5}" y="24" width="11" height="14" rx="3" fill="${d['cap'+i]}" stroke="#0002"/>`).join('')}</svg>`;
  renderCart();                                       // a basket line drawn at boot now gets its art
  document.querySelectorAll('[data-flag-section]').forEach(el => el.hidden = false);
  document.querySelectorAll('.flagstage').forEach(st => st.insertAdjacentHTML('afterbegin', ribbonSVG()));
  const draw = () => document.querySelectorAll('[data-flag-art]').forEach(svg => buildKeychain(svg, d));
  draw();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(draw);
  // what is on it, key by key — written from FLAG_KEYS so it cannot disagree with the picture
  document.querySelectorAll('[data-flag-legend]').forEach(ul => ul.innerHTML = FLAG_KEYS.map(([ch, cap, letter], i) =>
    `<li><span class="mk" style="background:${d['cap'+i]};color:${d['letter'+i]}">${ch}</span>
       <span>${ch === '♥' ? 'Heart' : ch} <small>${letter.toLowerCase()} on ${cap.toLowerCase()}</small></span></li>`).join('')
    + `<li><span class="mk base" style="background:${d.base}"></span><span>Base <small>white</small></span></li>`);
  document.querySelectorAll('[data-flag-price]').forEach(el => el.textContent = money(fp.p));
  document.querySelectorAll('[data-flag-days]').forEach(el => el.textContent = flagDaysText(flagDays()));
  document.querySelectorAll('[data-flag-href]').forEach(a => a.href = PAP ? PAP.flagUrl : 'flag-day.html');
  // Before orders open the clicker is a teaser: price and a "coming soon" pill
  // instead of Add to cart. The theme's "Flag Day: take orders" switch opens it.
  const open = PAP ? PAP.flagOpen === true : CONFIG.flagOpen;
  document.querySelectorAll('[data-flag-soon]').forEach(el => el.hidden = open);
  document.querySelectorAll('[data-flag-add]').forEach(b => {
    b.hidden = !open;
    if (!open) return;
    if (fp.av === false) { b.disabled = true; b.textContent = 'Sold out'; return; }
    b.addEventListener('click', () => {
      track('pap_shelf_add_attempt', {});
      const ex = cart.find(x => x.kind === 'shelf' && x.id === FLAG_ID);
      const cap = stockCap(fp);
      if (cap != null && ex && ex.qty >= cap) {
        toast(`Only ${cap} in stock — they're all in your cart`); openCart(true); return;
      }
      if (ex) ex.qty++;
      else cart.push({kind:'shelf', id:FLAG_ID, n:fp.t, price:fp.p, qty:1, variant:fp.v, img:fp.img});
      save(); renderCart(); openCart(true);
    });
  });
  // The keys really press, like the real thing. Decoration on top of the
  // picture, so it stays off the keyboard and out of the accessibility tree.
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.addEventListener('pointerdown', e => {
    const k = e.target.closest('.presskeys .key'); if (!k) return;
    k.classList.add('down'); setTimeout(() => k.classList.remove('down'), 140);
    const stage = k.closest('.flagstage'); if (!stage || still) return;
    // confetti in the pressed key's colour, with the flag's own mixed in
    const i = [...k.parentNode.querySelectorAll('.key')].indexOf(k);
    const kr = k.getBoundingClientRect(), sr = stage.getBoundingClientRect();
    const cols = [d['cap'+i], d['cap'+i], d['letter'+i], 'var(--uae-red)', 'var(--uae-green)', 'var(--uae-black)'];
    for (let n = 0; n < 14; n++) {
      const c = document.createElement('i'), a = Math.random() * Math.PI * 2, r = 50 + Math.random() * 70;
      c.className = 'confetto';
      c.style.cssText = `left:${kr.left - sr.left + kr.width/2}px;top:${kr.top - sr.top + kr.height/3}px;` +
        `background:${cols[n % cols.length]};--dx:${Math.cos(a)*r}px;--dy:${Math.sin(a)*r - 40}px;--r:${Math.random()*540 - 270}deg`;
      stage.append(c); setTimeout(() => c.remove(), 1000);
    }
  });
})();

/** The flag in motion: one silk ribbon in the flag's colours — red, green,
    white, black — zig-zagging up behind the clicker. Each band is the same curve
    shifted down a band's width; their control points drift together, so the
    ribbon slowly breathes. `slice` crops rather than stretches, and nothing
    moves for visitors who asked for less motion. */
function ribbonSVG(){
  const cols = ['#E8202A', '#00843D', '#FFFFFF', '#1B1B1F'], b = 17;
  const wave = (k, m) => { const o = k * b;
    return `M-60 ${262 + o}C140 ${318 + o - m} 300 ${36 + o + m} 660 ${84 + o - m}`; };   // an S: low left, up behind the clicker, out high right
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const g = cols.map((c, k) => `<path d="${wave(k, 0)}" fill="none" stroke="${c}" stroke-width="${b + .8}">${still ? ''
    : `<animate attributeName="d" dur="9s" repeatCount="indefinite" calcMode="spline"
        keySplines=".45 0 .55 1;.45 0 .55 1" values="${wave(k, 0)};${wave(k, 38)};${wave(k, 0)}"/>`}</path>`).join('');
  return `<svg class="ribbon" viewBox="0 0 600 340" preserveAspectRatio="xMidYMid slice" aria-hidden="true">${g}</svg>`;
}
